/**
 * Domain enums. These mirror the Prisma enums in packages/db one-to-one
 * (consolidated across spec v1.0 → v1.7). They are expressed as string-literal
 * unions plus a runtime const array so engines can both type-check and iterate.
 * Keep this file and the Prisma schema in lockstep.
 */

export const STORE_REGIONS = ["EU", "US", "CA"] as const;
export type StoreRegion = (typeof STORE_REGIONS)[number];

export const ROLES = ["ADMIN", "MANAGER", "PACKER"] as const;
export type Role = (typeof ROLES)[number];

/**
 * Order lifecycle. v1.0 base states plus addendum additions:
 *  - PARTIALLY_FULFILLED / READY_FOR_PICKUP / PICKED_UP (§29.12, v1.1)
 *  - AWAITING_STOCK (§32.5, v1.2)
 *  - AWAITING_CS_DECISION (§35.5, v1.3 — hold sub-state for shortages)
 */
export const ORDER_STATUSES = [
  "NEW",
  "ROUTED",
  "READY",
  "IN_BATCH",
  "PACKING",
  "PACKED",
  "FULFILLED",
  "ON_HOLD",
  "EXCEPTION",
  "CANCELLED",
  "PARTIALLY_FULFILLED",
  "READY_FOR_PICKUP",
  "PICKED_UP",
  "AWAITING_STOCK",
  "AWAITING_CS_DECISION",
] as const;
export type OrderStatus = (typeof ORDER_STATUSES)[number];

export const SHIPMENT_STATUSES = [
  "PENDING",
  "LABELLED",
  "HANDED_OVER",
  "IN_TRANSIT",
  "DELIVERED",
  "VOIDED",
  "FAILED",
] as const;
export type ShipmentStatus = (typeof SHIPMENT_STATUSES)[number];

export const WEIGHT_FLAGS = ["OK", "UNDER", "OVER", "MANUAL"] as const;
export type WeightFlag = (typeof WEIGHT_FLAGS)[number];

export const READINESS_EFFECTS = ["REQUIRE", "BLOCK"] as const;
export type ReadinessEffect = (typeof READINESS_EFFECTS)[number];

export const IOR_TYPES = ["CUSTOMER", "ENTITY"] as const;
export type IorType = (typeof IOR_TYPES)[number];

export const VALUE_BASES = ["ORDER_PRICE", "SHOPIFY_COGS", "CUSTOM_PRICE_LIST"] as const;
export type ValueBasis = (typeof VALUE_BASES)[number];

export const FULFILMENT_MODES = ["LABEL_FIRST", "PACK_FIRST"] as const;
export type FulfilmentMode = (typeof FULFILMENT_MODES)[number];

/** §29.4 / §37 — local pickup retained in the model but deferred in build. */
export const FULFILMENT_TYPES = [
  "CARRIER_SHIPMENT",
  "CARRIER_PICKUP_POINT",
  "LOCAL_PICKUP",
] as const;
export type FulfilmentType = (typeof FULFILMENT_TYPES)[number];

/** §30 — service class dominates queue priority (express first). */
export const SERVICE_CLASSES = ["EXPRESS", "PRIORITY", "STANDARD", "ECONOMY"] as const;
export type ServiceClass = (typeof SERVICE_CLASSES)[number];

export const BATCH_STATUSES = ["DRAFT", "PROCESSING", "PARTIAL", "COMPLETE", "FAILED"] as const;
export type BatchStatus = (typeof BATCH_STATUSES)[number];

export const BATCH_ITEM_STATUSES = [
  "PENDING",
  "LABEL_CREATED",
  "PRINTED",
  "PACKED",
  "FAILED",
  "SKIPPED",
  "VOIDED",
] as const;
export type BatchItemStatus = (typeof BATCH_ITEM_STATUSES)[number];

export const SHORTAGE_STATUSES = ["AWAITING_CS_DECISION", "RESOLVED"] as const;
export type ShortageStatus = (typeof SHORTAGE_STATUSES)[number];

export const SHORTAGE_DECISIONS = [
  "SHIP_PARTIAL",
  "WAIT_FOR_RESTOCK",
  "REMOVE_ITEM",
  "CANCEL_ORDER",
] as const;
export type ShortageDecision = (typeof SHORTAGE_DECISIONS)[number];

export const SHORTAGE_REASON_CODES = ["OUT_OF_STOCK", "DAMAGED", "CANT_LOCATE", "OTHER"] as const;
export type ShortageReasonCode = (typeof SHORTAGE_REASON_CODES)[number];

export const SHORTAGE_POLICIES = ["SHIP_PARTIAL", "HOLD_WHOLE"] as const;
export type ShortagePolicy = (typeof SHORTAGE_POLICIES)[number];

export const INVENTORY_REASONS = [
  "PACK_SHORTAGE",
  "CYCLE_COUNT",
  "DAMAGE",
  "MANUAL_CORRECTION",
] as const;
export type InventoryReason = (typeof INVENTORY_REASONS)[number];

export const NOTIFICATION_CHANNELS = ["SLACK", "EMAIL", "INTERCOM", "WEBHOOK"] as const;
export type NotificationChannel = (typeof NOTIFICATION_CHANNELS)[number];

export const PACK_EVENT_TYPES = [
  "SLIP_SCANNED",
  "ITEM_SCANNED",
  "ITEM_MISMATCH",
  "ITEM_UNPACKED",
  "ITEM_SHORT",
  "MANUAL_CONFIRM",
  "BOX_SET",
  "WEIGHED",
  "WEIGHT_OVERRIDE",
  "PHOTO_CAPTURED",
  "TRACKING_CONFIRMED",
  "LABEL_CREATED",
  "LABEL_FAILED",
  "PARKED",
  "RESUMED",
] as const;
export type PackEventType = (typeof PACK_EVENT_TYPES)[number];

/** Standardized order-level exception reasons surfaced in the Exceptions view. */
export const EXCEPTION_REASONS = [
  "UNROUTABLE",
  "UNMAPPED_METHOD",
  "MISSING_COGS",
  "MISSING_PRICE_LIST_ENTRY",
  "MISSING_HS_CODE",
  "MISSING_ORIGIN_COUNTRY",
  "ADDRESS_UNCONFIRMED",
  "FULFILMENT_PUSH_FAILED",
  "LABEL_FAILED",
  "STOCK_SHORTAGE",
  "AWAITING_STOCK",
  "LOCAL_PICKUP_UNSUPPORTED",
] as const;
export type ExceptionReason = (typeof EXCEPTION_REASONS)[number];
